import React, { useState } from "react";
import Reveal from "./Reveal";
import { Plus } from "lucide-react";

const services = [
  {
    no: "01",
    title: "Personal Brand Strategy",
    short: "Positioning, audience clarity and a content roadmap built around your unique advantage.",
    points: ["Platform positioning", "Content pillars & narrative", "12 month growth roadmap"],
  },
  {
    no: "02",
    title: "Content Production",
    short: "Filming, editing, design and copy. Produced with the craft of an in house creative team.",
    points: ["Long and short form filming", "Editing and motion design", "Editorial copywriting"],
  },
  {
    no: "03",
    title: "Social Media Growth",
    short: "Organic distribution that compounds. Posting, engagement and platform native craft.",
    points: ["Daily posting and scheduling", "Community management", "Algorithm aware reformatting"],
  },
  {
    no: "04",
    title: "Paid Ads & Monetisation",
    short: "From audience to revenue. Funnels, offers and paid amplification on top of the brand.",
    points: ["Funnel architecture", "High ticket offer design", "Paid acquisition and retargeting"],
  },
];

const Row = ({ s, open, onToggle, idx }) => {
  return (
    <div
      className="border-t border-[var(--lt-line)]"
      data-testid={`service-row-${idx}`}
    >
      <button
        onClick={onToggle}
        data-testid={`service-toggle-${idx}`}
        className="w-full grid grid-cols-12 gap-4 py-8 lg:py-10 text-left items-center group"
      >
        <span className="col-span-2 lg:col-span-1 lt-num text-2xl lg:text-3xl">{s.no}</span>
        <span
          className="col-span-8 lg:col-span-8 font-display tracking-editorial text-[var(--lt-charcoal)] leading-tight"
          style={{ fontSize: "clamp(1.6rem, 3vw, 2.6rem)", fontWeight: 500 }}
        >
          {s.title}
        </span>
        <span className="hidden lg:block col-span-2 text-sm text-[var(--lt-bark)]/70">
          {open ? "Close" : "Explore"}
        </span>
        <span className="col-span-2 lg:col-span-1 flex justify-end">
          <Plus
            size={28}
            className={`text-[var(--lt-charcoal)] transition-transform duration-500 ${
              open ? "rotate-45" : ""
            }`}
          />
        </span>
      </button>

      <div
        className={`grid transition-all duration-500 ease-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="grid grid-cols-12 gap-4 pb-10">
            <div className="col-span-12 lg:col-span-1" />
            <div className="col-span-12 lg:col-span-7">
              <p className="text-[var(--lt-bark)] text-base lg:text-lg leading-relaxed max-w-xl">
                {s.short}
              </p>
            </div>
            <ul className="col-span-12 lg:col-span-4 flex flex-col gap-2">
              {s.points.map((p) => (
                <li
                  key={p}
                  className="text-sm tracking-[0.04em] text-[var(--lt-charcoal)]/80 border-b border-[var(--lt-line)] pb-2"
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-28 lg:py-36 bg-[var(--lt-peace)]"
    >
      <div className="container mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-16 lg:mb-20">
          <div className="col-span-12 lg:col-span-4">
            <Reveal>
              <p className="eyebrow mb-4">/ Services</p>
              <h2
                className="font-display tracking-editorial text-[var(--lt-charcoal)] leading-[1.05]"
                style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)", fontWeight: 600 }}
              >
                Branding <em className="italic text-[var(--lt-bark)]">beyond</em> content.
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-6 flex items-end">
            <Reveal delay={180}>
              <p className="text-[var(--lt-bark)] text-base lg:text-lg max-w-xl">
                A focused set of services. Strategy at the centre, content and distribution
                around it, monetisation on top. Designed to be retained, not transactional.
              </p>
            </Reveal>
          </div>
        </div>

        <Reveal>
          <div className="border-b border-[var(--lt-line)]">
            {services.map((s, i) => (
              <Row
                key={s.no}
                s={s}
                idx={i}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Services;
